import { ApolloClient, gql, useApolloClient } from "@apollo/client";
import type {
  CellId,
  MagicCompletionEventId,
  ScopeItemType,
} from "@hex/common";
import { AppSessionId, HEX_PRIVATE_PREFIX } from "@hex/common";
import { editor as Editor, Position, languages } from "monaco-editor";
import React, { useContext, useMemo } from "react";

import { BroadcastCompletionProvider } from "./BroadcastCompletionProvider";
import {
  GetCompletionsDocument,
  GetCompletionsQuery,
  GetCompletionsQueryVariables,
} from "./KernelCompletionProvider.generated";

gql`
  query GetCompletions(
    $appSessionId: AppSessionId!
    $source: String!
    $position: Int!
  ) {
    getCompletions(
      appSessionId: $appSessionId
      source: $source
      position: $position
    ) {
      matches {
        name
        type
      }
      cursorStart
      cursorEnd
    }
  }
`;

const getKind = (kind: string): languages.CompletionItemKind => {
  switch (kind) {
    case "function":
      return languages.CompletionItemKind.Function;
    case "keyword":
      return languages.CompletionItemKind.Keyword;
    case "class":
      return languages.CompletionItemKind.Class;
    case "module":
      return languages.CompletionItemKind.Module;
    case "statement":
    case "instance":
    default:
      return languages.CompletionItemKind.Variable;
  }
};

export interface KernelCompletionData {
  name: string;
  type: ScopeItemType;
}

export interface MagicCompletionData {
  context: string;
  prefix: string;
  suffix: string;
  cellId: CellId;
  completion: string;
  completionId: MagicCompletionEventId;
  duration: number;
  startOffset: number | null;
  endOffset: number | null;
  providerCompletionId: string | null;
  accepted: boolean;
  displayed: boolean;
}

export class KernelCompletion extends BroadcastCompletionProvider<KernelCompletionData> {
  triggerCharacters = ["."];

  constructor(private client: ApolloClient<unknown>) {
    super();
  }
  private appSessionId?: AppSessionId;

  public setAppSessionId(appSessionId: AppSessionId): void {
    this.appSessionId = appSessionId;
  }

  async provideCompletionItems(
    model: Editor.ITextModel,
    position: Position,
  ): Promise<languages.CompletionList> {
    if (!this.appSessionId || model.isDisposed()) {
      return { suggestions: [] };
    }
    const word = model.getWordAtPosition(position);
    const source = model.getValue();
    const offset = model.getOffsetAt(position);
    const { data } = await this.client.query<
      GetCompletionsQuery,
      GetCompletionsQueryVariables
    >({
      query: GetCompletionsDocument,
      variables: {
        appSessionId: this.appSessionId,
        source,
        position: offset,
      },
    });
    // model can be disposed while the above query is running
    if (model.isDisposed()) {
      return { suggestions: [] };
    }
    let startLineNumber = position.lineNumber;
    let endLineNumber = position.lineNumber;

    let startColumn = word ? word.startColumn : position.column;
    let endColumn = word ? word.endColumn : position.column;
    if (data.getCompletions.cursorStart) {
      const startPos = model.getPositionAt(data.getCompletions.cursorStart);
      startLineNumber = startPos.lineNumber;
      startColumn = startPos.column;
    }
    if (data.getCompletions.cursorEnd) {
      const endPos = model.getPositionAt(data.getCompletions.cursorEnd);
      endLineNumber = endPos.lineNumber;
      endColumn = endPos.column;
    }
    const existingCompletions = new Set<string>();
    return {
      suggestions: (data?.getCompletions.matches || [])
        // Filter completions already provided by parameter completion provider
        .filter((match) => !existingCompletions.has(match.name))
        // Filter the built in magics (most of them don't even work)
        .filter((match) => !match.name.startsWith("%"))
        // Filter out hex internal variables
        .filter((match) => !match.name.startsWith(HEX_PRIVATE_PREFIX))
        .map((match) => ({
          label: match.name,
          insertText: match.name,
          kind: getKind(match.type),
          range: {
            startLineNumber,
            endLineNumber,
            startColumn,
            endColumn,
          },
        })),
    };
  }
}

const KernelCompletionContext = React.createContext<KernelCompletion | null>(
  null,
);

export const KernelCompletionProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const client = useApolloClient();
  const kernelCompletion = useMemo(
    () => new KernelCompletion(client),
    [client],
  );
  return (
    <KernelCompletionContext.Provider value={kernelCompletion}>
      {children}
    </KernelCompletionContext.Provider>
  );
};

export const useKernelCompletion = (): KernelCompletion => {
  const context = useContext(KernelCompletionContext);
  if (!context) {
    throw new Error("KernelCompletion not found");
  }
  return context;
};
